import { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import apiClient from '../connections/api'
import AuthService from '../services/auth.service'
import usersRoutes from '../routes/users.routes'
import { clearAllDatabases } from '../indexedDB'

export const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [userData, setUserData] = useState(undefined)
    const [dispositivoIdData, setDispositivoIdData] = useState('')
    const [datosSesion, setDatosSesion] = useState()
    const [justLoggedIn, setJustLoggedIn] = useState(false)
    const navigate = useNavigate()
    const location = useLocation()

    const clearJustLoggedInFlag = () => setJustLoggedIn(false)

    const refreshSessionToken = async (newToken = null) => {
        try {
            const tokenToUse = newToken || localStorage.getItem('token')
            if (!tokenToUse) {
                console.warn('[AuthContext] No token available to refresh.')
                return false
            }

            localStorage.setItem('token', tokenToUse)
            apiClient.defaults.headers.common.Authorization = `Bearer ${tokenToUse}`

            const currentUserId = userData?._id || userData?.id
            if (currentUserId) {
                try {
                    const response = await usersRoutes.getUser(currentUserId)
                    if (response?.data) {
                        const fetchedUser = response.data
                        const updatedUser = { ...fetchedUser, _id: fetchedUser.id || fetchedUser._id }
                        setUserData(updatedUser)
                        localStorage.setItem('user', JSON.stringify(updatedUser))
                    }
                } catch (e) {
                    console.warn('[AuthContext] Could not fetch updated user details from server:', e)
                }
            }

            console.log('[AuthContext] ✅ Session token and permissions refreshed successfully (IndexedDB preserved).')
            return true
        } catch (error) {
            console.error('[AuthContext] Error refreshing session token:', error)
            return false
        }
    }

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('token')
            const userFromStorage = localStorage.getItem('user')

            if (token && userFromStorage) {
                try {
                    const user = JSON.parse(userFromStorage)
                    setUserData(user) // Set user data in AuthContext after initial check
                    setIsAuthenticated(true)
                    apiClient.defaults.headers.common.Authorization = `Bearer ${token}` // Set token for API calls
                } catch (e) {
                    console.error('Error parsing data from localStorage', e)
                    await logout(true)
                }
            } else {
                setIsAuthenticated(false)
                if (location.pathname !== '/login') {
                    navigate('/login', { replace: true })
                }
            }
            setLoading(false)
        }

        checkAuth()

        // pautasDatabase.borrarDb();

        let deviceId = localStorage.getItem('deviceId')
        if (!deviceId || deviceId.length === 0) {
            deviceId = `device-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            localStorage.setItem('deviceId', deviceId)
        }
        setDispositivoIdData(deviceId)
    }, [])

    const login = async (username, password) => {
        return new Promise(resolve => {
            AuthService.login(username, password, dispositivoIdData)
                .then(async (result) => {
                    if (result.changePasswordRequired) {
                        resolve({
                            state: true,
                            changePasswordRequired: true,
                            user: result.user,
                            passProvisory: result.passProvisory,
                            originalPassword: password // Lo necesitaremos para el endpoint de cambio
                        })
                        return
                    }

                    const { user, token, session } = result
                    // Create a complete user object with the _id property that the app expects
                    const completeUser = { ...user, _id: user.id }

                    setUserData(completeUser)
                    localStorage.setItem('user', JSON.stringify(completeUser))
                    localStorage.setItem('token', token)
                    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`
                    localStorage.setItem('isauthenticated', 'true')

                    setDatosSesion(session)
                    localStorage.setItem('currentSessionData', JSON.stringify(session))

                    setIsAuthenticated(true)
                    setJustLoggedIn(true)
                    resolve({
                        state: true,
                        response: { data: { user: completeUser, access_token: token } }
                    })
                })
                .catch(error => {
                    localStorage.setItem('isauthenticated', 'false')
                    console.error('Login error details:', error.response ? error.response.data : error)
                    alert('Credenciales incorrectas o cuenta no habilitada.')
                    setIsAuthenticated(false)
                    resolve({ state: false })
                })
        })
    }

    const logout = async (skipConfirmation = false) => {
        if (!skipConfirmation) {
            if (!confirm('Confirme salida de la aplicación. Para volver a iniciar sesión requiere contar con internet para validar las credenciales.')) {
                return // User cancelled logout
            }
        }

        if (userData) {
            try {
                const currentSessionData = JSON.parse(localStorage.getItem('currentSessionData'))
                await AuthService.logout(userData, currentSessionData, dispositivoIdData)
            } catch (error) {
                console.error('Error during server-side logout:', error)
            }
            localStorage.removeItem('currentSessionData')
        }

        // Clear all IndexedDB databases
        try {
            await clearAllDatabases()
            console.log('All local databases cleared successfully.')
        } catch (error) {
            console.error('Error clearing local databases:', error)
        }

        const deviceId = localStorage.getItem('deviceId') // Preserve deviceId across logout
        window.localStorage.clear()
        if (deviceId) {
            localStorage.setItem('deviceId', deviceId)
        }

        navigate('/', { replace: true })
        setIsAuthenticated(false)
        setUserData(undefined)
        setDatosSesion(undefined)
        apiClient.defaults.headers.common.Authorization = null // Clear token from API client
    }

    const value = {
        isAuthenticated,
        loading,
        userData,
        dispositivoIdData,
        login,
        logout,
        refreshSessionToken,
        setUserData,
        setDispositivoIdData,
        datosSesion,
        justLoggedIn,
        clearJustLoggedInFlag
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)
